// components/Modals/LiveStudioModal/LiveStudioModal.js
import React, { useState, useEffect } from 'react';
import './LiveStudioModal.css';
import { toast } from 'react-hot-toast';
import { API_BASE_URL_EXPORT } from '../../../config';

const LiveStudioModal = ({ isOpen, onClose, onSubmit, editingStudio, API_URL }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    stream_link: '',
    alternative_link: '',
    cover_image: null
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingStudio) {
      setFormData({
        title: editingStudio.title || '',
        description: editingStudio.description || '',
        stream_link: editingStudio.stream_link || '',
        alternative_link: editingStudio.alternative_link || '',
        cover_image: null
      });
      if (editingStudio.cover_image) {
        const baseURL = API_URL.split('/api')[0];
        setPreviewImage(`${API_BASE_URL_EXPORT}/uploads/${editingStudio.cover_image}`);
      }
    } else {
      resetForm();
    }
  }, [editingStudio, API_URL]);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      stream_link: '',
      alternative_link: '',
      cover_image: null
    });
    setPreviewImage(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Sadece resim dosyaları yüklenebilir!');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Dosya boyutu 10MB\'dan küçük olmalıdır!');
        return;
      }
      setFormData(prev => ({
        ...prev,
        cover_image: file
      }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      cover_image: null
    }));
    setPreviewImage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Başlık zorunludur!');
      return;
    }

    if (!formData.stream_link.trim()) {
      toast.error('Yayın Linki zorunludur!');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const submitData = new FormData();
      
      submitData.append('title', formData.title.trim());
      submitData.append('description', formData.description.trim());
      submitData.append('stream_link', formData.stream_link.trim());
      submitData.append('alternative_link', formData.alternative_link.trim());
      
      if (formData.cover_image) {
        submitData.append('cover_image', formData.cover_image);
      }

      const url = editingStudio 
        ? `${API_URL}/live-studio/${editingStudio.id}`
        : `${API_URL}/live-studio`;
      
      const method = editingStudio ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: submitData
      });

      const data = await response.json();

      if (data.success) {
        toast.success(editingStudio ? 'İçerik güncellendi!' : 'İçerik eklendi!');
        onSubmit();
        resetForm();
      } else {
        toast.error(data.error || 'İşlem başarısız');
      }
    } catch (error) {
      console.error('Hata:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content live-studio-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{editingStudio ? '✏️ Canlı Stüdyo Düzenle' : '➕ Yeni Canlı Stüdyo Ekle'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="title">Başlık <span className="required">*</span></label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Canlı yayın başlığı"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Alt Bilgi</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Canlı yayın açıklaması"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label htmlFor="stream_link">Yayın Linki <span className="required">*</span></label>
              <input
                type="url"
                id="stream_link"
                name="stream_link"
                value={formData.stream_link}
                onChange={handleInputChange}
                placeholder="https://example.com/live"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="alternative_link">Alternatif Yayın Linki</label>
              <input
                type="url"
                id="alternative_link"
                name="alternative_link"
                value={formData.alternative_link}
                onChange={handleInputChange}
                placeholder="https://example.com/live-alt"
              />
            </div>

            <div className="form-group">
              <label htmlFor="cover_image">Yayın Kapağı</label>
              
              {previewImage && (
                <div className="image-preview">
                  <img src={previewImage} alt="Preview" />
                  <button type="button" onClick={handleRemoveImage} className="remove-image-btn">✕ Resmi Kaldır</button>
                </div>
              )}

              <input
                type="file"
                id="cover_image"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />
              
              {!previewImage && (
                <label htmlFor="cover_image" className="file-upload-label">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                  <span>Kapak Resmi Seç</span>
                </label>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={loading}>İptal</button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Kaydediliyor...' : (editingStudio ? 'Güncelle' : 'Ekle')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LiveStudioModal;